import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  endOfDay,
  format,
  getDaysInMonth,
  isAfter,
  isBefore,
  startOfDay,
  startOfWeek,
} from "date-fns";

import { repeats } from "../../../../types/types";

// Backend RepeatOn ids (see RecurringWorkService / WorkService.AddRepeatWorkAsync)
export const REPEAT_ON_IDS = {
  immediately: "dd6b35fd-01ff-48cf-9f58-407727de6930",
  specific: "e6aca4e9-e91b-4314-98f4-254cf05effcf",
  completion: "4093e0b1-452f-458f-8bfc-e8956b92d95f",
} as const;

const REPEAT_TYPE_IDS = {
  weekly: repeats[0].id,
  monthly: repeats[1].id,
  quarterly: repeats[2].id,
  yearly: repeats[3].id,
  never: repeats[4].id,
};

export type Frequency =
  | "never"
  | "weekly"
  | "biweekly"
  | "monthly"
  | "quarterly"
  | "annually";
export type CreateMode = keyof typeof REPEAT_ON_IDS;
export type DayOfMonth = number | "last";
export type PeriodOffset = "same" | "following";

export type RecurringScheduleForm = {
  frequency: Frequency;
  every: number;
  createMode: CreateMode;
  createWeekday: number;
  dueWeekday: number;
  dueWeekOffset: PeriodOffset;
  createDay: DayOfMonth;
  dueDay: DayOfMonth;
  dueMonthOffset: PeriodOffset;
  fiscalStartMonth: number;
  createQuarterMonth: number;
  dueQuarterMonth: number;
  createMonth: number;
  dueMonth: number;
  dueYearOffset: PeriodOffset;
  repeatUntil: Date;
};

export type ScheduleWork = {
  startDate: Date | string;
  dueDate: Date | string;
};

export type Occurrence = { startDate: Date; dueDate: Date };

export type RepeatWorkPayload = {
  repeatTypeEnumId: string;
  beforeDays: number;
  repeatOnId: string;
  repeatValue: string;
  repeatUntil: Date;
  dueDate: string;
  beforeStartDate: number;
  beforeStartDateValue: string;
  dueOnDate?: number;
};

export const WEEKDAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];
export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export const MONTHLY_EVERY_OPTIONS = [1, 2, 3, 6];

export const ordinal = (n: number) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  const suffix = ["th", "st", "nd", "rd"][n % 10] ?? "th";
  return `${n}${n % 10 > 3 ? "th" : suffix}`;
};

export const DAY_OF_MONTH_OPTIONS: { value: DayOfMonth; label: string }[] = [
  ...Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: ordinal(i + 1),
  })),
  { value: "last", label: "Last day" },
];

const MAX_SEARCH_PERIODS = 400;

/** A day in the given month, clamped to the month's length ("31st" in April is the 30th). */
export const dayInMonth = (year: number, month: number, day: DayOfMonth) => {
  const firstOfMonth = new Date(year, month, 1);
  const lastDay = getDaysInMonth(firstOfMonth);
  return new Date(
    year,
    month,
    day === "last" ? lastDay : Math.min(day, lastDay),
  );
};

const toDay = (value: Date | string) => startOfDay(new Date(value));

/**
 * "On completion": how many periods after the completion the next work is due
 * (bi-weekly is due 2 weeks later, "every 3 months" 3 months later).
 */
export const completionPeriodsLater = (form: RecurringScheduleForm) =>
  form.frequency === "biweekly"
    ? 2
    : form.frequency === "monthly"
    ? Math.max(1, form.every)
    : 1;

/** Start/due dates of the n-th period counted from the period containing `from`. */
const occurrenceForPeriod = (
  form: RecurringScheduleForm,
  from: Date,
  n: number,
): Occurrence => {
  const followingSteps =
    form.createMode === "completion" ? completionPeriodsLater(form) : 1;
  switch (form.frequency) {
    case "weekly":
    case "biweekly": {
      const week = addWeeks(startOfWeek(from), n);
      return {
        startDate: addDays(week, form.createWeekday),
        dueDate: addDays(
          addWeeks(
            week,
            form.dueWeekOffset === "following" ? followingSteps : 0,
          ),
          form.dueWeekday,
        ),
      };
    }
    case "monthly": {
      const month = addMonths(new Date(from.getFullYear(), from.getMonth()), n);
      const dueMonth = addMonths(
        month,
        form.dueMonthOffset === "following" ? followingSteps : 0,
      );
      return {
        startDate: dayInMonth(
          month.getFullYear(),
          month.getMonth(),
          form.createDay,
        ),
        dueDate: dayInMonth(
          dueMonth.getFullYear(),
          dueMonth.getMonth(),
          form.dueDay,
        ),
      };
    }
    case "quarterly": {
      const monthsSinceFiscalStart =
        (from.getMonth() - form.fiscalStartMonth + 12) % 12;
      const quarterStart = addMonths(
        new Date(from.getFullYear(), from.getMonth()),
        -(monthsSinceFiscalStart % 3) + n * 3,
      );
      const startMonth = addMonths(quarterStart, form.createQuarterMonth);
      // "On completion" is due in the quarter after the one it was completed in.
      const dueMonth = addMonths(
        quarterStart,
        form.dueQuarterMonth + (form.createMode === "completion" ? 3 : 0),
      );
      return {
        startDate: dayInMonth(
          startMonth.getFullYear(),
          startMonth.getMonth(),
          form.createDay,
        ),
        dueDate: dayInMonth(
          dueMonth.getFullYear(),
          dueMonth.getMonth(),
          form.dueDay,
        ),
      };
    }
    case "annually": {
      const year = from.getFullYear() + n;
      return {
        startDate: dayInMonth(year, form.createMonth, form.createDay),
        dueDate: dayInMonth(
          form.dueYearOffset === "following" ? year + 1 : year,
          form.dueMonth,
          form.dueDay,
        ),
      };
    }
    default:
      return { startDate: from, dueDate: from };
  }
};

/** "On completion" is always due in the following period (the greyed-out boxes). */
export const withCompletionRules = (
  form: RecurringScheduleForm,
): RecurringScheduleForm =>
  form.createMode === "completion"
    ? {
        ...form,
        dueWeekOffset: "following",
        dueMonthOffset: "following",
        dueYearOffset: "following",
      }
    : form;

/** The due date must not fall before the day the work is created. */
export const isDueBeforeStart = (form: RecurringScheduleForm) => {
  if (form.frequency === "never" || form.createMode === "completion") {
    return false;
  }
  const sample = occurrenceForPeriod(form, new Date(2026, 0, 15), 0);
  return isBefore(sample.dueDate, sample.startDate);
};

/**
 * The first work item the schedule will create: the earliest occurrence that
 * comes after the current work item (so it never duplicates it) and that
 * doesn't start in the past.
 */
export const getFirstOccurrence = (
  scheduleForm: RecurringScheduleForm,
  work: ScheduleWork,
  today: Date = new Date(),
): Occurrence | null => {
  if (scheduleForm.frequency === "never") return null;
  const form = withCompletionRules(scheduleForm);

  const workStart = toDay(work.startDate);
  const workDue = toDay(work.dueDate);
  const todayDay = startOfDay(today);
  const from = isAfter(workStart, todayDay) ? workStart : todayDay;
  const searchFrom = isBefore(workStart, from) ? workStart : from;

  for (let n = 0; n < MAX_SEARCH_PERIODS; n++) {
    const occurrence = occurrenceForPeriod(form, searchFrom, n);
    const isAfterCurrentWork = isAfter(occurrence.dueDate, workDue);

    if (form.createMode === "completion") {
      if (isAfterCurrentWork) return occurrence;
      continue;
    }

    if (
      isAfterCurrentWork &&
      isAfter(occurrence.startDate, workStart) &&
      !isBefore(occurrence.startDate, todayDay)
    ) {
      return occurrence;
    }
  }
  return null;
};

const REPEAT_VALUE: Record<
  Exclude<Frequency, "never">,
  (f: RecurringScheduleForm) => number
> = {
  weekly: () => 1,
  biweekly: () => 2,
  monthly: (f) => f.every,
  quarterly: () => 1,
  annually: () => 1,
};

const REPEAT_TYPE: Record<Exclude<Frequency, "never">, string> = {
  weekly: REPEAT_TYPE_IDS.weekly,
  biweekly: REPEAT_TYPE_IDS.weekly,
  monthly: REPEAT_TYPE_IDS.monthly,
  quarterly: REPEAT_TYPE_IDS.quarterly,
  annually: REPEAT_TYPE_IDS.yearly,
};

/** Most work items "Immediately" may create at once (same as the server and Ask; REC-012). */
export const MAX_IMMEDIATE_COPIES = 60;

/**
 * How many work items "Immediately" creates: one per period from the first due date up to
 * "Repeat until" (the server counts the same way). Stops counting at `stopAfter`.
 */
export const countImmediateCopies = (
  form: RecurringScheduleForm,
  first: Occurrence,
  stopAfter = 1000,
): number => {
  const until = endOfDay(form.repeatUntil);
  const step = (date: Date, n: number): Date => {
    switch (form.frequency) {
      case "weekly":
        return addWeeks(date, n);
      case "biweekly":
        return addWeeks(date, 2 * n);
      case "monthly":
        return addMonths(date, Math.max(1, form.every) * n);
      case "quarterly":
        return addMonths(date, 3 * n);
      case "annually":
        return addYears(date, n);
      default:
        return until;
    }
  };
  if (form.frequency === "never") return 0;
  let count = 0;
  while (count < stopAfter && !isAfter(step(first.dueDate, count), until)) {
    count++;
  }
  return count;
};

export type ScheduleValidation =
  | {
      ok: true;
      first: Occurrence;
      payload: RepeatWorkPayload;
      /** "Immediately" only: how many work items saving creates at once. */
      copies?: number;
    }
  | {
      ok: false;
      error: string | null;
      /** Due before start: shown as the "Invalid Due Date" message on Save. */
      isInvalidDueDate?: boolean;
    };

/**
 * Settings the backend needs to place each new work item exactly (start day,
 * due day in the following period, fiscal year, time zone). Saved as JSON in
 * the schedule's beforeStartDateValue field; see RecurringSchedule.cs.
 */
export const buildScheduleSettings = (
  scheduleForm: RecurringScheduleForm,
  first: Occurrence,
) => {
  const form = withCompletionRules(scheduleForm);
  const dueDay = form.dueDay === "last" ? 31 : form.dueDay;
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = undefined;
  }
  const isWeekly = form.frequency === "weekly" || form.frequency === "biweekly";
  return {
    v: 1,
    tz: timeZone,
    tzOffset: -first.dueDate.getTimezoneOffset(),
    ...(form.createMode !== "completion" && !isWeekly
      ? {
          startDay: form.createDay === "last" ? 31 : form.createDay,
          startMonthsBeforeDue: differenceInCalendarMonths(
            first.dueDate,
            first.startDate,
          ),
        }
      : {}),
    ...(isWeekly ? { dueWeekday: form.dueWeekday } : { dueDay }),
    ...(form.frequency === "quarterly"
      ? {
          fiscalStartMonth: form.fiscalStartMonth,
          dueQuarterMonth: form.dueQuarterMonth,
        }
      : {}),
    ...(form.frequency === "annually" ? { dueMonth: form.dueMonth } : {}),
  };
};

/** Turns the form into the body the repeat-work endpoint expects. */
export const buildRepeatPayload = (
  form: RecurringScheduleForm,
  work: ScheduleWork,
  today: Date = new Date(),
): ScheduleValidation => {
  if (form.frequency === "never") return { ok: false, error: null };

  if (isDueBeforeStart(form)) {
    return {
      ok: false,
      error: "The due date can't be earlier than the start date.",
      isInvalidDueDate: true,
    };
  }

  const first = getFirstOccurrence(form, work, today);
  if (!first) {
    return { ok: false, error: "Couldn't work out the next occurrence." };
  }

  if (isBefore(endOfDay(form.repeatUntil), first.dueDate)) {
    return {
      ok: false,
      error: `“Repeat until” must be on or after the first due date (${first.dueDate.toLocaleDateString(
        "en-US",
        { month: "short", day: "numeric", year: "numeric" },
      )}).`,
    };
  }

  const copies =
    form.createMode === "immediately"
      ? countImmediateCopies(form, first)
      : undefined;
  if (copies !== undefined && copies > MAX_IMMEDIATE_COPIES) {
    return {
      ok: false,
      error: `“Immediately” would create ${copies} work items at once. The limit is ${MAX_IMMEDIATE_COPIES}: choose an earlier “Repeat until” date or another option.`,
    };
  }

  const leadDays =
    form.createMode === "completion"
      ? Math.max(
          0,
          differenceInCalendarDays(toDay(work.dueDate), toDay(work.startDate)),
        )
      : differenceInCalendarDays(first.dueDate, first.startDate);

  return {
    ok: true,
    first,
    copies,
    payload: {
      repeatTypeEnumId: REPEAT_TYPE[form.frequency],
      repeatValue: String(REPEAT_VALUE[form.frequency](form)),
      repeatOnId: REPEAT_ON_IDS[form.createMode],
      dueDate: first.dueDate.toISOString(),
      beforeDays: leadDays,
      // Work is created on its start date ("This will also be the start date").
      beforeStartDate: 0,
      beforeStartDateValue: JSON.stringify(buildScheduleSettings(form, first)),
      repeatUntil: endOfDay(form.repeatUntil),
      // Due day of the month, so "Last day" stays the last day of every month.
      ...(form.frequency === "monthly" ||
      form.frequency === "quarterly" ||
      form.frequency === "annually"
        ? { dueOnDate: form.dueDay === "last" ? 31 : form.dueDay }
        : {}),
    },
  };
};

const DEFAULT_REPEAT_YEARS: Record<Frequency, number> = {
  never: 1,
  weekly: 1,
  biweekly: 1,
  monthly: 1,
  quarterly: 2,
  annually: 5,
};

/** Default end date: long enough for several occurrences of the chosen frequency. */
export const getDefaultRepeatUntil = (
  frequency: Frequency,
  today: Date = new Date(),
) => addYears(startOfDay(today), DEFAULT_REPEAT_YEARS[frequency]);

/** Defaults for a new schedule, taken from the current work item where possible. */
export const getDefaultForm = (
  work: ScheduleWork,
  today: Date = new Date(),
): RecurringScheduleForm => {
  const start = toDay(work.startDate);
  const due = toDay(work.dueDate);
  const dueWeekOffset: PeriodOffset =
    differenceInCalendarDays(startOfWeek(due), startOfWeek(start)) > 0
      ? "following"
      : "same";
  const dueMonthOffset: PeriodOffset =
    due.getFullYear() * 12 + due.getMonth() >
    start.getFullYear() * 12 + start.getMonth()
      ? "following"
      : "same";

  return {
    frequency: "never",
    every: 1,
    createMode: "specific",
    createWeekday: start.getDay(),
    dueWeekday: due.getDay(),
    dueWeekOffset,
    createDay: start.getDate(),
    dueDay: due.getDate(),
    dueMonthOffset,
    fiscalStartMonth: 0,
    // With a January fiscal year, quarters start in Jan/Apr/Jul/Oct.
    createQuarterMonth: start.getMonth() % 3,
    dueQuarterMonth: due.getMonth() % 3,
    createMonth: start.getMonth(),
    dueMonth: due.getMonth(),
    dueYearOffset:
      due.getFullYear() > start.getFullYear() ? "following" : "same",
    repeatUntil: getDefaultRepeatUntil("never", today),
  };
};

export type ExistingSchedule = {
  repeatTypeEnumId?: string | null;
  repeatValue?: number | null;
  repeatOnId?: string | null;
  repeatUntil?: Date | string | null;
  beforeStartDateValue?: string | null;
};

type SavedSettings = Partial<{
  startDay: number;
  dueWeekday: number;
  dueDay: number;
  fiscalStartMonth: number;
  dueQuarterMonth: number;
  dueMonth: number;
}>;

const parseSavedSettings = (value?: string | null): SavedSettings => {
  if (!value || !value.trim().startsWith("{")) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const toDayOfMonth = (day: number | undefined, fallback: DayOfMonth) =>
  day === undefined ? fallback : day >= 31 ? "last" : day;

/**
 * Pre-fills the form from a running schedule. Days come from the newest work
 * item in the series, the rest from the schedule settings.
 */
export const getFormFromSchedule = (
  schedule: ExistingSchedule,
  latestWork: ScheduleWork,
  today: Date = new Date(),
): RecurringScheduleForm => {
  const base = getDefaultForm(latestWork, today);
  const typeId = schedule.repeatTypeEnumId?.toLowerCase();
  const repeatValue = schedule.repeatValue || 1;

  const frequency: Frequency =
    typeId === REPEAT_TYPE_IDS.weekly
      ? repeatValue === 2
        ? "biweekly"
        : "weekly"
      : typeId === REPEAT_TYPE_IDS.monthly
      ? "monthly"
      : typeId === REPEAT_TYPE_IDS.quarterly
      ? "quarterly"
      : typeId === REPEAT_TYPE_IDS.yearly
      ? "annually"
      : "never";

  const repeatOnId = schedule.repeatOnId?.toLowerCase();
  const createMode =
    (Object.keys(REPEAT_ON_IDS) as CreateMode[]).find(
      (mode) => REPEAT_ON_IDS[mode] === repeatOnId,
    ) ?? "specific";

  const saved = parseSavedSettings(schedule.beforeStartDateValue);

  return {
    ...base,
    createDay: toDayOfMonth(saved.startDay, base.createDay),
    dueDay: toDayOfMonth(saved.dueDay, base.dueDay),
    dueWeekday: saved.dueWeekday ?? base.dueWeekday,
    fiscalStartMonth: saved.fiscalStartMonth ?? base.fiscalStartMonth,
    dueQuarterMonth: saved.dueQuarterMonth ?? base.dueQuarterMonth,
    dueMonth: saved.dueMonth ?? base.dueMonth,
    frequency,
    createMode,
    every: MONTHLY_EVERY_OPTIONS.includes(repeatValue) ? repeatValue : 1,
    repeatUntil: schedule.repeatUntil
      ? startOfDay(new Date(schedule.repeatUntil))
      : getDefaultRepeatUntil(frequency, today),
  };
};

/** "weekly", "every 2 weeks", "monthly", "every 3 months", ... */
export const describeFrequency = (
  repeatType: string | null | undefined,
  repeatValue: number | null | undefined,
) => {
  const type = (repeatType || "").toLowerCase();
  const every = repeatValue && repeatValue > 1 ? repeatValue : 1;
  const unit =
    type === "weekly"
      ? "week"
      : type === "monthly"
      ? "month"
      : type === "quarterly"
      ? "quarter"
      : type === "yearly"
      ? "year"
      : null;
  if (!unit) return type;
  return every === 1 ? type : `every ${every} ${unit}s`;
};

const FREQUENCY_LABELS: Record<Frequency, string> = {
  never: "Never",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

const CREATE_MODE_LABELS: Record<CreateMode, string> = {
  immediately: "all created at once",
  specific: "created on a set day",
  completion: "next one when this is completed",
};

/** One line for a schedule that hasn't started yet (New work window). */
export const describeScheduleForm = (form: RecurringScheduleForm) => {
  const unit = { monthly: "months", quarterly: "quarters", annually: "years" };
  const frequency =
    form.every > 1 && form.frequency in unit
      ? `Every ${form.every} ${unit[form.frequency as keyof typeof unit]}`
      : FREQUENCY_LABELS[form.frequency];
  return `${frequency} · ${
    CREATE_MODE_LABELS[form.createMode]
  } · until ${format(form.repeatUntil, "MMM d, yyyy")}`;
};
